// ─────────────────────────────────────────────────────────────
// CHURCH TEAM READY™ — all copy and links live here. Image URLs live in data/images.ts.
// Same structure as ABA Session Ready / Team Meeting Ready (modeled on Managers), new palette and button texts.
// Promise (must match ads, checkout and the PDF): 52-week training plan + 10 complete team trainings,
// $27 one-time, 10-day guarantee, the 5 bonuses in the checkout order.
// Values = the checkout: main $159; bonuses $37, $27, $27, $27, $37 ($155); total $314 → $27, you save $287.
// Church niche: Bible-based and interdenominational, no doctrine debates, no safety or growth promises.
// Name the role (pastors, ministry leaders), never personal attributes of the reader.
// Huddle names quoted below are the ones printed in the Gamma Studio PDF (doc 97xb1l17hlawg93).
// ─────────────────────────────────────────────────────────────

export const CHECKOUT_URL = 'https://church-team-ready.impultienda.ar/checkout';
/** Meta pixel: "pixel church team ready" (stock 4). InitiateCheckout fires ONLY on the offer button. */
export const META_PIXEL_ID = '1092697286701548';

export const PAGE_TITLE = 'Church Team Ready™ | Your Whole Year of Volunteer Team Trainings, Already Planned';
export const META_DESCRIPTION =
  'A 52-week volunteer training plan plus 10 complete, ready-to-lead trainings for your church teams. Open this month’s training, prepare in 10 minutes and lead it.';

// ── GLOBAL ───────────────────────────────────────────────────
export const brand = 'CHURCH TEAM READY™';
/** Label for every button that scrolls to the offer section */
export const ctaLabel = 'GET MY TEAM READY';
export const topLabel = 'GET READY';
export const ctaMicrocopy = 'Instant PDF · One-time $27 · 10-day guarantee';
export const stickyLabel = 'GET MY TEAM READY · $27';
export const price = '$27';

// ── 1. HERO (buyer line, title, short subtitle, mockup, button) ──
export const hero = {
  eyebrow: 'FOR PASTORS, VOLUNTEER COORDINATORS & MINISTRY LEADERS',
  titleLead: 'YOUR WHOLE YEAR OF VOLUNTEER TRAININGS,',
  titleHighlight: 'ALREADY PLANNED.',
  sub: 'A 52-week training plan plus 10 complete, ready-to-lead team trainings. Open this month’s training, prepare in 10 minutes and lead it.',
  mockupAlt: 'Church Team Ready™ on a desktop screen, with the 5 bonuses on tablets and phones',
};

// ── 2. STATS ─────────────────────────────────────────────────────
export const stats = [
  { value: '52', label: 'WEEKS PLANNED' },
  { value: '10', label: 'COMPLETE TRAININGS' },
  { value: '10 MIN', label: 'TO PREPARE EACH ONE' },
  { value: '$27', label: 'ONE-TIME PAYMENT' },
];

// ── 2b. BEFORE / AFTER ──────────────────────────────────────────────
export const beforeAfter = {
  title: 'SATURDAY NIGHT VS. SUNDAY MORNING.',
  before: {
    label: 'BEFORE',
    when: 'Saturday night',
    quote: '“What do I teach my volunteers this month?”',
    items: ['A training written from scratch.', 'Volunteers who aren’t sure what to do.', 'Empty spots nobody planned for.'],
  },
  after: {
    label: 'AFTER',
    when: 'Sunday morning',
    items: ['Open this month’s training.', 'Prepare in 10 minutes.', 'Lead the 45-minute training.', 'Every volunteer knows their role.'],
  },
};

// ── 2c. ONE SUNDAY, EVERY TEAM READY (new element for this landing) ──
export const sunday = {
  eyebrow: 'ONE SUNDAY. EVERY TEAM READY.',
  title: 'EVERY VOLUNTEER KNOWS WHERE TO BE AND WHAT TO SAY.',
  sub: 'This is what a Sunday looks like when every team has been trained with the same system.',
  note: 'Example Sunday. Adapt the times to your own service.',
  items: [
    { time: '8:30', team: 'TEAM LEADS', text: 'Run the 10-minute Sunday huddle: welcome, one update, assignments, encouragement, prayer.' },
    { time: '8:45', team: 'PARKING & DOOR GREETERS', text: 'In place, name tags on.', say: '“Good morning! I’m Sarah. Is this your first time with us?”' },
    { time: '8:50', team: 'MEDIA & SOUND', text: 'Line check done. Every slide checked against the order of service.' },
    { time: '9:00', team: 'KIDS CHECK-IN', text: 'Matching tags ready. Two approved adults in every room.', say: '“Does your child have any allergies we should know about?”' },
    { time: '9:15', team: 'USHERS', text: 'Late arrivals seated between songs. Exits clear, first-aid kit checked.' },
    { time: 'AFTER', team: 'PRAYER & FOLLOW-UP', text: 'Prayer at the front. Guest cards collected and a same-day text sent before tonight.', say: '“How can I pray for you today?”' },
  ],
};

// ── 3. WHICH TEAM NEEDS IT FIRST (selector with an example training) ──
const block = 'Training block: open & devotional 5 · teach the role 12 · team practice 15 · Sunday walk-through 8 · pray & send 5';
export const selector = {
  eyebrow: 'START WITH THE TEAM THAT NEEDS IT MOST',
  title: 'WHAT’S HARD ON SUNDAY RIGHT NOW?',
  sub: 'Pick the problem and see the training that is ready for it.',
  startLabel: 'START HERE',
  tablistLabel: 'Sunday problems',
  columns: ['PAGE 1 · PLAN', 'PAGE 2 · LEAD IT', 'PAGE 3 · SUNDAY & MESSAGES'],
  note: 'All 10 trainings follow this same 3-page structure. Adapt each one to your church, your building and your policies.',
  items: [
    {
      tab: 'Guests feel lost when they arrive',
      n: 'TRAINING 01 · WEEK 1',
      title: 'Welcome & Hospitality: The First 7 Minutes',
      goal: 'Every guest is noticed, greeted, helped and seated with warmth in their first 7 minutes on campus.',
      open: ['The goal, the team and 10-minute prep', 'Your opening words, word for word, and a short devotional', block],
      activity: ['The guest path: parking, door, lobby, kids check-in, seat', 'Team activity: The Guest Walk, with 3 debrief questions', 'Role sheets: parking team, door greeter, info desk host, seat host'],
      close: ['Sunday checklist: before, during and after the service', 'What to say at the door, at kids check-in and at the seat', 'Ready-to-send team reminder and thank-you message'],
    },
    {
      tab: 'Kids check-in feels risky',
      n: 'TRAINING 06 · WEEK 22',
      title: 'Kids Ministry & Safe Check-In',
      goal: 'Every child is welcomed, checked in and picked up the same safe way, every time, with two approved adults in every room.',
      open: ['The goal, the team and 10-minute prep', 'Your opening words, word for word, and a short devotional', block],
      activity: ['The safe check-in path, from greeting to pick-up with the matching tag', 'Team activity: Pretend Family, with 3 debrief questions', 'Role sheets: check-in host, room leader, room helper, hallway monitor'],
      close: ['Sunday checklist: before, during and after the service', 'What to say to a first-time family, to the child and at pick-up', 'Ready-to-send team reminder and parent message'],
    },
    {
      tab: 'The tech keeps failing',
      n: 'TRAINING 05 · WEEK 18',
      title: 'Media & Sound',
      goal: 'Every service runs with clear sound, slides on time and a calm plan for when something fails.',
      open: ['The goal, the team and 10-minute prep', 'Your opening words, word for word, and a short devotional', block],
      activity: ['The booth routine, from power on to power down', 'Team activity: Plan B Drill, with 3 debrief questions', 'Role sheets: sound tech, slides, livestream, camera & lights'],
      close: ['Sunday checklist: before, during and after the service', 'Headset cues and what to say to the pastor before the service', 'Ready-to-send team reminder and thank-you message'],
    },
    {
      tab: 'We never have enough volunteers',
      n: 'TRAINING 09 · WEEK 35',
      title: 'Recruiting & Keeping Volunteers',
      goal: 'Every team lead invites new volunteers personally, gets them started well and keeps them serving with healthy rotations.',
      open: ['The goal, the team and 10-minute prep', 'Your opening words, word for word, and a short devotional', block],
      activity: ['The volunteer cycle: invite, start, train, thank, rotate, grow', 'Team activity: The Invitation, plus a load check of the schedule', 'Role sheets: coordinator, team lead, volunteer buddy, scheduler'],
      close: ['Sunday checklist: before, during and after the service', 'The personal invitation, the first Sunday and the stepping-back talk', 'Ready-to-send invitation follow-up and thank-you message'],
    },
  ],
};

// ── 4. LOOK INSIDE (real pages of the book, in the same order as pageImages) ──
export const lookInside = {
  eyebrow: 'REAL PAGES. SEE THE FORMAT BEFORE YOU BUY.',
  title: 'SEE WHAT YOU’LL HAVE IN FRONT OF YOU.',
  sub: 'Open any page and read it at full size: your 52-week plan, a complete training, the Sunday tools and the training planner.',
  hint: 'Swipe to see more · Tap any page to view it full size',
  note: 'These are selected pages, not the full guide.',
  captions: [
    'Your 52-week plan (Q1)',
    'Training 01 · plan',
    'Training 01 · lead it',
    'Training 01 · Sunday & messages',
    'When Sunday gets hard',
    'Plan any training in 10 minutes',
  ],
};

// ── 5. THE SYSTEM ──────────────────────────────
export const plan = {
  eyebrow: 'ONE SYSTEM FOR EVERY TEAM',
  title: 'THE WHOLE YEAR IS PLANNED. YOU JUST OPEN IT AND LEAD IT.',
  text: 'Every week of the year is mapped: a team focus, a goal and a ready 10-minute Sunday huddle, or the start of one of the 10 complete trainings. Each training gives you the plan, the meeting and the Sunday tools in 3 pages.',
  steps: [
    { title: 'OPEN', text: 'Find this week in your 52-week plan.' },
    { title: 'PREPARE', text: 'Read the 3 pages and print the role sheets in 10 minutes.' },
    { title: 'LEAD IT', text: 'Devotional, teach the role, practice together, walk through Sunday.' },
    { title: 'SEND', text: 'Send the reminder before Sunday and the thank-you after.' },
  ],
  featuresTitle: 'IN EVERY COMPLETE TRAINING',
  features: [
    { icon: 'goal', title: 'One clear goal for Sunday', text: 'What the team will do differently, who it’s for and the week to start it.' },
    { icon: 'timer', title: 'A 45-minute training block', text: 'Devotional 5 · Teach the role 12 · Team practice 15 · Sunday walk-through 8 · Pray & send 5. Or a 30-minute version.' },
    { icon: 'steps', title: 'A team activity that sticks', text: 'Setup, steps and 3 debrief questions, so volunteers practice instead of only listening.' },
    { icon: 'questions', title: 'Role sheets for every position', text: 'One card per role with what to do before, during and after the service.' },
    { icon: 'quote', title: 'What to say, word for word', text: 'Scripts for the door, kids check-in, prayer, follow-up calls and hard moments.' },
    { icon: 'action', title: 'Sunday checklist + messages', text: 'A printable Sunday checklist and ready-to-send reminder and thank-you messages.' },
  ],
} as const;

// ── 6. THE 52-WEEK PLAN ───────────────────────────────────────
export const yearPlan = {
  eyebrow: 'YOUR 52-WEEK TRAINING PLAN',
  title: 'THE WHOLE YEAR, MAPPED WEEK BY WEEK.',
  sub: '★ weeks start a new training. ◆ weeks are team reviews. Every other week has a team focus, a goal and a ready 10-minute Sunday huddle.',
  quarters: [
    {
      q: 'Q1',
      weeks: 'WEEKS 1–13',
      name: 'BUILD YOUR FRONT DOOR',
      full: [
        { week: '1', label: 'START TRAINING 01', title: 'Welcome & Hospitality' },
        { week: '5', label: 'START TRAINING 02', title: 'Ushers & Basic Safety' },
        { week: '9', label: 'START TRAINING 03', title: 'Guest Follow-Up' },
        { week: '13', label: 'TEAM REVIEW', title: 'Review every team with the planner', review: true },
      ],
      activities: '+ 9 ready 10-minute Sunday huddles, like The 10-Foot Rule, Walk, Don’t Point and Safety Scan.',
    },
    {
      q: 'Q2',
      weeks: 'WEEKS 14–26',
      name: 'STRENGTHEN SUNDAY',
      full: [
        { week: '14', label: 'START TRAINING 04', title: 'Worship Team' },
        { week: '18', label: 'START TRAINING 05', title: 'Media & Sound' },
        { week: '22', label: 'START TRAINING 06', title: 'Kids Ministry & Safe Check-In' },
        { week: '26', label: 'TEAM REVIEW', title: 'Review every team with the planner', review: true },
      ],
      activities: '+ 9 ready 10-minute Sunday huddles, like Ready by Call Time, Plan B Drill and Two-Adult Check.',
    },
    {
      q: 'Q3',
      weeks: 'WEEKS 27–39',
      name: 'CONNECT AND CARE',
      full: [
        { week: '27', label: 'START TRAINING 07', title: 'New Member Integration' },
        { week: '31', label: 'START TRAINING 08', title: 'Prayer Team' },
        { week: '35', label: 'START TRAINING 09', title: 'Recruiting & Keeping Volunteers' },
        { week: '39', label: 'TEAM REVIEW', title: 'Review every team with the planner', review: true },
      ],
      activities: '+ 9 ready 10-minute Sunday huddles, like Connector Match, Prayer Walk and Load Check.',
    },
    {
      q: 'Q4',
      weeks: 'WEEKS 40–52',
      name: 'LEAD AND MULTIPLY',
      full: [
        { week: '40', label: 'START TRAINING 10', title: 'Team Leadership' },
        { week: '52', label: 'TEAM REVIEW', title: 'Year-end review', review: true },
      ],
      activities: '+ 11 ready 10-minute Sunday huddles, like Run the Huddle, Full House Plan and Thank-You Circle.',
    },
  ],
  toolsTitle: 'PLUS YOUR TEAM TOOLS',
  tools: ['When Sunday gets hard: 8 situations, what to say and do', 'Team training planner: plan any training in 10 minutes', 'Sunday field card for every team lead'],
};

// ── 7. THE 10 COMPLETE TRAININGS ────────────────────────────────
export const meetings = {
  eyebrow: 'THE 10 COMPLETE TRAININGS',
  title: 'TEN VOLUNTEER TEAM TRAININGS. READY TO LEAD.',
  sub: 'Follow the 52-week plan, or start with the team that needs it most. Each one is 3 pages: plan, lead it, Sunday & messages.',
  weekLabel: 'START WEEK',
  items: [
    { n: '01', week: '1', title: 'Welcome & Hospitality: The First 7 Minutes', goal: 'Every guest is noticed, greeted, helped and seated with warmth.' },
    { n: '02', week: '5', title: 'Ushers & Basic Safety', goal: 'Seat people well, run a calm offering and know who does what in an emergency.' },
    { n: '03', week: '9', title: 'Guest Follow-Up', goal: 'Every first-time guest hears from you within 48 hours, with one simple next step.' },
    { n: '04', week: '14', title: 'Worship Team', goal: 'Arrive prepared, rehearse with purpose and lead the congregation as one team.' },
    { n: '05', week: '18', title: 'Media & Sound', goal: 'Clear sound, slides on time and a calm Plan B when something fails.' },
    { n: '06', week: '22', title: 'Kids Ministry & Safe Check-In', goal: 'One safe way to check in and pick up every child, with two adults in every room.' },
    { n: '07', week: '27', title: 'New Member Integration', goal: 'A person, a group and a place to serve within the first 90 days.' },
    { n: '08', week: '31', title: 'Prayer Team', goal: 'Listen with care, pray simply and keep every request private.' },
    { n: '09', week: '35', title: 'Recruiting & Keeping Volunteers', goal: 'Personal invitations, a good first Sunday and healthy rotations.' },
    { n: '10', week: '40', title: 'Team Leadership', goal: 'Weekly huddles, clear assignments, calm hard conversations and the next leader.' },
  ],
  cta: 'GET ALL 10 TRAININGS + 5 BONUSES',
  ctaMicrocopy: '$27 USD · One-time payment',
};

// ── 8. BONUSES (same order and values as the checkout) ──────────────
export const bonuses = {
  eyebrow: '5 BONUSES INCLUDED IN YOUR $27 GUIDE',
  title: 'EVERYTHING ELSE YOUR TEAMS NEED.',
  sub: 'Devotionals for every week, onboarding, thank-you messages, scheduling and an emergency plan, ready to use.',
  free: 'FREE',
  totalNote: '$155 in bonuses, included free',
  items: [
    {
      label: 'BONUS 01',
      value: '$37',
      short: '52 Team Devotionals',
      hook: 'Open every meeting in 5 minutes.',
      title: '52 Team Devotionals — A 5-Minute Opener for Every Volunteer Meeting',
      text: 'One short devotional for every week of the year: a Bible reference, a simple thought about serving, one question for the team and a one-line prayer. Read it out loud and start your meeting.',
    },
    {
      label: 'BONUS 02',
      value: '$27',
      short: 'New Volunteer Welcome Kit',
      hook: 'Know what to do when someone says “I want to serve”.',
      title: 'New Volunteer Welcome Kit — Welcome Letter, Role Agreement & First-Sunday Plan',
      text: 'A ready-to-send welcome letter, a simple interest form, a one-page role agreement, a first-Sunday plan, a shadowing checklist and a 30-day check-in conversation.',
    },
    {
      label: 'BONUS 03',
      value: '$27',
      short: '30 Thank-You Messages for Volunteers',
      hook: 'Thank people by name, every week.',
      title: '30 Thank-You Messages for Volunteers — Ready to Send, Print or Say',
      text: 'Text messages, short emails, card notes and words to say from the stage, for every moment: a busy Sunday, a first month, a big event, a hard season and the end of the year.',
    },
    {
      label: 'BONUS 04',
      value: '$27',
      short: 'Volunteer Scheduling Kit',
      hook: 'Stop filling empty spots on Saturday night.',
      title: 'Volunteer Scheduling Kit — Rotation Plans, Reminder Texts & No-Show Backup Plan',
      text: 'Rotation plans for every team, a sign-up sheet, reminder texts for the week before and the day before, swap messages and a simple no-show backup plan.',
    },
    {
      label: 'BONUS 05',
      value: '$37',
      short: 'Sunday Emergency Response Guide',
      hook: 'Know who does what if something goes wrong.',
      title: 'Sunday Emergency Response Guide — Medical, Lost Child, Fire & Severe Weather Plans',
      text: 'One-page response plans for a medical emergency, a lost child, a fire alarm, severe weather and a disruptive person: who calls for help, who leads and what to say. Adapt it to your building and local laws.',
    },
  ],
};

// ── 9. OFFER ─────────────────────────────────────────────────────
export const offer = {
  eyebrow: 'YOUR TEAM’S YEAR STARTS HERE',
  title: 'YOUR WHOLE YEAR OF VOLUNTEER TRAININGS. ALL IN ONE GUIDE.',
  kicker: 'THE COMPLETE DIGITAL GUIDE',
  name: 'Church Team Ready™',
  description: 'A 52-week training plan and 10 complete, ready-to-lead volunteer team trainings, plus five bonuses: devotionals, onboarding, thank-you messages, scheduling and an emergency plan.',
  mockupAlt: 'Everything included in Church Team Ready™',
  includedTitle: 'YOUR GUIDE INCLUDES',
  included: [
    '52-week training plan: the whole year mapped',
    '10 complete team trainings, 3 pages each',
    'A 45-minute training block, plus a 30-minute version',
    'A team activity and role sheets for every position',
    'What to say on Sunday, word for word',
    'Sunday checklist and ready-to-send messages in each',
    'Team tools: hard moments, planner, Sunday field card',
  ],
  /** Value stack: exactly the checkout prices */
  stackTitle: 'EVERYTHING YOU GET',
  mainItem: { label: 'Church Team Ready™ (52-week plan + 10 complete trainings)', value: '$159' },
  totalLabel: 'Total value',
  totalValue: '$314',
  todayLabel: 'TODAY',
  priceNote: 'One-time payment · About 52¢ a week',
  saveNote: 'You save $287',
  /** The ONLY button that goes to checkout */
  cta: 'GET THE WHOLE YEAR — $27',
  ctaNote: ['Continue to secure checkout', 'Access sent by email after payment'],
  format: 'English-language PDF. Digital delivery. Bible-based and interdenominational. Adapt every training to your church, your building, your policies and local laws.',
  guaranteeNote: '10-day money-back guarantee',
};

// ── 10. GUARANTEE ────────────────────────────────────────────
export const guarantee = {
  sealTop: '10-DAY',
  sealBottom: 'MONEY-BACK',
  title: '10-DAY MONEY-BACK. NO QUESTIONS ASKED.',
  text: "Use it with your team this month. If it isn't right for your church, email support@stockdigital.shop within 10 days of purchase and we'll give your money back.",
};

// ── 11. FAQ ───────────────────────────────────────────────────
export const faq = {
  eyebrow: 'KNOW EXACTLY WHAT YOU’RE GETTING',
  title: 'A FEW QUESTIONS BEFORE YOU GET STARTED.',
  sub: 'Built for pastors, volunteer coordinators and ministry team leaders of small and mid-size churches. A ready-to-lead training guide, not a video course.',
  items: [
    {
      q: 'What exactly is this product?',
      a: 'An English-language PDF guide: a 52-week training plan (every week has a team focus, a goal and a ready 10-minute Sunday huddle, or the start of a training) and 10 complete volunteer team trainings of 3 pages each, plus team tools and 5 bonuses.',
    },
    {
      q: 'Who is it for?',
      a: 'Pastors, associate pastors, volunteer coordinators, first impressions directors, kids ministry leaders, worship leaders, production leads and team leads of small and mid-size churches.',
    },
    {
      q: 'Will it fit my church?',
      a: 'It is Bible-based and interdenominational. It focuses on practical service (welcome, care, prayer, teamwork) and stays away from doctrinal debates, so it works in most evangelical and non-denominational churches. Scripture quotes are from the King James Version.',
    },
    {
      q: 'Is it a legal or safety standard?',
      a: 'No. It is a training resource. Adapt every training, especially kids check-in and emergencies, to your church’s own policies, your building and local laws.',
    },
    {
      q: 'How do I receive it?',
      a: 'After your payment is approved, access is sent to the email address you enter at checkout. Nothing is shipped. Use an email you can access and check your spam folder if needed.',
    },
    {
      q: 'How long is each training?',
      a: 'Each training is built for a 45-minute team meeting: devotional 5, teach the role 12, team practice 15, Sunday walk-through 8, pray & send 5. Short on time? Every training also runs in 30 minutes. On the other weeks, the 10-minute Sunday huddle fits before the service.',
    },
    {
      q: 'Can I use the trainings individually?',
      a: 'Yes. Each training stands alone, so you can start with the team that needs it most, or follow the 52-week plan from week 1.',
    },
    {
      q: 'Can I print it for my volunteers?',
      a: 'Yes. You can print the role sheets, checklists and messages for the volunteers of your own church. Sharing the PDF outside your church, redistributing it or reselling it is not included.',
    },
    { q: 'Is it a subscription?', a: 'No. One payment of $27 USD.' },
    { q: 'How does the guarantee work?', a: 'You have 10 days to request a refund, no questions asked.' },
  ],
};

// ── 12. FINAL CTA ─────────────────────────────────────────────
export const finalCta = {
  eyebrow: 'FOR THE NEXT “WHAT DO I TEACH MY VOLUNTEERS THIS MONTH?”',
  title: 'HAVE YOUR NEXT TEAM TRAINING READY IN 10 MINUTES.',
  text: 'Open this month’s training. Prepare in 10 minutes. Lead it with your team, and walk into Sunday with a plan.',
  microcopy: '52-week plan + 10 complete trainings + 5 bonuses · $27 USD, one-time payment',
};

// ── 13. FOOTER ──────────────────────────────────────────────────
export const footer = {
  line: 'CHURCH TEAM READY™ · Ready-to-lead volunteer team trainings for churches.',
  supportLabel: 'Support:',
  supportEmail: 'support@stockdigital.shop',
  copyright: '© 2026',
};
